import {
  attendee,
  content,
  eventInfo,
  forms,
  sessions,
  speakers,
} from '../data/mockEvent'
import type {
  Attendee,
  ContentItem,
  EventForm,
  EventInfo,
  EventListItem,
  Session,
  Speaker,
} from '../data/types'
import { apiClient } from './api'

/** Nav availability + admin settings, from GET /events/{id}/nav-summary. */
export type NavVisibility = 'auto' | 'show' | 'hide'
export interface NavSummary {
  content: Record<string, boolean>
  settings: Record<string, { visibility: NavVisibility; ordinal: number }>
  keys: string[]
}

/**
 * Service layer for the attendee experience.
 *
 * Reads from the backend Event API (docs/vericast-events-api-spec.md). The API
 * returns camelCase shapes that already match ui/src/data/types.ts, so
 * responses are used directly.
 *
 * If the API is unreachable (e.g. offline demo, or backend not yet deployed),
 * each call gracefully falls back to the bundled demo dataset so the UI still
 * renders. The resolved event id is cached for sub-resource calls.
 */

const USE_API = true

// The active event is scoped by slug (set from the /e/:slug route). When no
// slug is set (legacy /app or offline demo), we fall back to /events/current.
let activeSlug: string | null = null
let cachedEventId: string | null = null

/** Point the service at a specific event by slug (from the route). */
export function setActiveEventSlug(slug: string | null): void {
  if (slug !== activeSlug) {
    activeSlug = slug
    cachedEventId = null // invalidate the id cache when the event changes
  }
}

export function getActiveEventSlug(): string | null {
  return activeSlug
}

async function fetchActiveEvent(): Promise<EventInfo> {
  const path = activeSlug
    ? `/events/by-slug/${encodeURIComponent(activeSlug)}`
    : '/events/current'
  const { data } = await apiClient.get<EventInfo>(path)
  cachedEventId = data.id
  return data
}

export async function resolveEventId(): Promise<string> {
  if (cachedEventId) return cachedEventId
  const data = await fetchActiveEvent()
  return data.id
}

/** Wrap an API call with a fallback to mock data on any failure. */
async function withFallback<T>(
  apiCall: () => Promise<T>,
  fallback: T,
): Promise<T> {
  if (!USE_API) return fallback
  try {
    return await apiCall()
  } catch {
    return fallback
  }
}

export const eventService = {
  // Attendee identity is not yet served by the backend (auth is a later
  // phase), so this remains the demo attendee for now.
  getAttendee: (): Promise<Attendee> => Promise.resolve(attendee),

  getEvent: (): Promise<EventInfo> => withFallback(fetchActiveEvent, eventInfo),

  /** Public fetch of a single event by slug (for the pre-auth branded pages). */
  getEventBySlug: (slug: string): Promise<EventInfo> =>
    apiClient
      .get<EventInfo>(`/events/by-slug/${encodeURIComponent(slug)}`)
      .then((r) => r.data),

  /** Public directory of published events (for discovery on the landing page). */
  listPublishedEvents: (): Promise<EventListItem[]> =>
    withFallback(async () => {
      // The API defaults to published-only for this list.
      const { data } = await apiClient.get<EventListItem[]>('/events')
      return data
    }, []),

  getSessions: (): Promise<Session[]> =>
    withFallback(async () => {
      const id = await resolveEventId()
      const { data } = await apiClient.get<Session[]>(`/events/${id}/sessions`)
      return data
    }, sessions),

  /** Which built-in nav sections have content + admin visibility settings. */
  getNavSummary: (): Promise<NavSummary> =>
    withFallback(
      async () => {
        const id = await resolveEventId()
        const { data } = await apiClient.get<NavSummary>(`/events/${id}/nav-summary`)
        return data
      },
      { content: {}, settings: {}, keys: [] },
    ),

  getSession: (sessionId: string): Promise<Session | undefined> =>
    withFallback(async () => {
      const id = await resolveEventId()
      const { data } = await apiClient.get<Session>(
        `/events/${id}/sessions/${sessionId}`,
      )
      return data
    }, sessions.find((s) => s.id === sessionId)),

  getSpeakers: (): Promise<Speaker[]> =>
    withFallback(async () => {
      const id = await resolveEventId()
      const { data } = await apiClient.get<Speaker[]>(`/events/${id}/speakers`)
      return data
    }, speakers),

  getSpeaker: (speakerId: string): Promise<Speaker | undefined> =>
    withFallback(async () => {
      const id = await resolveEventId()
      const { data } = await apiClient.get<Speaker>(
        `/events/${id}/speakers/${speakerId}`,
      )
      return data
    }, speakers.find((s) => s.id === speakerId)),

  getContent: (): Promise<ContentItem[]> =>
    withFallback(async () => {
      const id = await resolveEventId()
      const { data } = await apiClient.get<ContentItem[]>(
        `/events/${id}/content`,
      )
      return data
    }, content),

  getForms: (): Promise<EventForm[]> =>
    withFallback(async () => {
      const id = await resolveEventId()
      const { data } = await apiClient.get<EventForm[]>(`/events/${id}/forms`)
      return data
    }, forms),

  getForm: (formId: string): Promise<EventForm | undefined> =>
    withFallback(async () => {
      const id = await resolveEventId()
      const { data } = await apiClient.get<EventForm>(
        `/events/${id}/forms/${formId}`,
      )
      return data
    }, forms.find((f) => f.id === formId)),
}
